import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Typography } from "@kelp-dao/kernel-design-system";
import { CalendarBlankIcon, CaretDownIcon } from "@phosphor-icons/react";
import { cn } from "@lib/utils";

// ============ CONFIGURATION ============
const INITIAL_MONTHS_TO_SHOW = 3; // Months shown on first load
const LOAD_MORE_INCREMENT = 6; // Months to add on "Load more"
const MAX_MONTHS_TO_SHOW = 36; // Maximum months to show (INITIAL + LOAD_MORE)
// =======================================

type TWeekData = {
  key: string;
  label: string;
  startDate: string;
  endDate: string;
};

type TMonthGroup = {
  year: number;
  month: string;
  monthFull: string;
  weeks: TWeekData[];
};

type TWeekYearPickerProps = {
  groupedByMonth: TMonthGroup[];
  selectedKey: string;
  onSelect: (key: string) => void;
};

// Get ordinal suffix for day
const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// Parse day from date string like "14th Jan 26"
const parseDay = (dateStr: string): number => {
  return parseInt(dateStr.trim().split(" ")[0]?.replace(/\D/g, "") || "1");
};

// Format display for selected date (e.g., "14 Jan - 22nd Jan, 2026")
const formatSelectedDisplay = (week: TWeekData, year: number): string => {
  const startDay = parseDay(week.startDate);
  const endDay = parseDay(week.endDate);
  const startMonth = week.startDate.split(" ")[1] || "";
  const endMonth = week.endDate.split(" ")[1] || "";

  return `${startDay}${getOrdinalSuffix(
    startDay
  )} ${startMonth} - ${endDay}${getOrdinalSuffix(endDay)} ${endMonth}, ${year}`;
};

const WeekYearPicker = ({
  groupedByMonth,
  selectedKey,
  onSelect,
}: TWeekYearPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonthsCount, setVisibleMonthsCount] = useState(
    Math.min(INITIAL_MONTHS_TO_SHOW, groupedByMonth.length)
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const totalMonths = groupedByMonth.length;

  // Visible month groups (all weeks in each month)
  const visibleMonthGroups = useMemo(() => {
    return groupedByMonth.slice(0, visibleMonthsCount);
  }, [groupedByMonth, visibleMonthsCount]);

  // Can we load more?
  const canLoadMore =
    visibleMonthsCount < totalMonths && visibleMonthsCount < MAX_MONTHS_TO_SHOW;

  // Have we reached the end?
  const hasReachedEnd = !canLoadMore;

  // Get selected week display
  const selectedDisplay = useMemo(() => {
    for (const group of groupedByMonth) {
      const week = group.weeks.find((w) => w.key === selectedKey);
      if (week) {
        return formatSelectedDisplay(week, group.year);
      }
    }
    return "";
  }, [groupedByMonth, selectedKey]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset visible months when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setVisibleMonthsCount(Math.min(INITIAL_MONTHS_TO_SHOW, totalMonths));
    }
  }, [isOpen, totalMonths]);

  const handleLoadMore = useCallback(() => {
    setVisibleMonthsCount((prev) =>
      Math.min(prev + LOAD_MORE_INCREMENT, MAX_MONTHS_TO_SHOW, totalMonths)
    );
  }, [totalMonths]);

  const handleSelect = useCallback(
    (week: TWeekData) => {
      onSelect(week.key);
      setIsOpen(false);
    },
    [onSelect]
  );

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      {/* Dropdown Button */}
      <button
        onClick={toggleDropdown}
        className="flex items-center gap-[17px] bg-white border border-border-default rounded p-2 hover:bg-bg-primary transition-colors"
      >
        <div className="flex items-center gap-1">
          <CalendarBlankIcon className="w-4 h-4 text-text-primary" />
          <Typography
            type="body"
            variant="xsmall"
            className="text-text-primary text-sm font-normal leading-[1.3] whitespace-nowrap"
          >
            {selectedDisplay}
          </Typography>
        </div>
        <CaretDownIcon
          className={cn(
            "w-4 h-4 text-text-primary transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-[0px_4px_24px_0px_rgba(0,0,0,0.25)] z-50 min-w-[192px] max-h-[300px] overflow-y-auto"
        >
          {visibleMonthGroups.map((group) => (
            <div key={`${group.month}-${group.year}`}>
              {/* Month Header */}
              <div className="px-3 pt-2 pb-1">
                <Typography
                  type="body"
                  variant="xsmall"
                  className="text-text-primary text-[12px] font-normal leading-[1.4] tracking-[-0.2px]"
                >
                  {group.monthFull}, {group.year}
                </Typography>
              </div>

              {/* All Week Items for this month */}
              {group.weeks.map((week) => {
                const isSelected = week.key === selectedKey;
                return (
                  <button
                    key={week.key}
                    onClick={() => handleSelect(week)}
                    className={cn(
                      "w-full text-left px-5 py-1.5 transition-colors",
                      isSelected
                        ? "bg-bg-primary text-text-primary font-medium"
                        : "bg-white text-text-secondary hover:bg-bg-primary hover:text-text-primary"
                    )}
                  >
                    <Typography
                      type="body"
                      variant="xsmall"
                      className={cn(
                        "text-xs leading-[1.4]",
                        isSelected
                          ? "text-text-primary font-medium"
                          : "text-text-secondary font-normal"
                      )}
                    >
                      {week.label}
                    </Typography>
                  </button>
                );
              })}
            </div>
          ))}

          {/* Load More Button */}
          {canLoadMore && (
            <div className="border-t border-border-default px-3 py-2">
              <button
                onClick={handleLoadMore}
                className="w-full text-center text-text-link text-xs font-medium hover:underline"
              >
                Load more
              </button>
            </div>
          )}

          {/* End Message */}
          {hasReachedEnd && (
            <div className="border-t border-border-default px-3 py-1.5">
              <Typography
                type="body"
                variant="xsmall"
                className="text-text-secondary text-[12px] font-normal leading-[1.4] tracking-[-0.2px]"
              >
                You&apos;ve reached the earliest available data.
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WeekYearPicker;
